/**
 * USPS address verification (Lob) for the `address` field — the widget's
 * verification, ported from formvu-web (`lib/lob/lob-client.ts` types,
 * `lib/lob/address-record.ts`, `form-fields/relax-address.ts`), so an embed
 * answer carries the same `_addressVerification` record a widget answer does.
 *
 * The widget calls Lob from the browser with the publishable key. The embed
 * runs on customers' sites, so it verifies through formvu-web's `POST /api/av`
 * instead (the key never ships in the bundle, and calls are throttled). Any
 * failure there is `null` — the caller records the address as unverified and
 * lets the visitor continue: verification never blocks a lead on our outage.
 */

// ----------------------------------------------------------------------
// Lob result (formvu-web lib/lob/lob-client.ts)
// ----------------------------------------------------------------------

export type LobDeliverability =
  | "deliverable"
  | "deliverable_missing_unit"
  | "deliverable_incorrect_unit"
  | "deliverable_unnecessary_unit"
  | "undeliverable";

export interface LobAddressComponents {
  primary_number: string;
  street_name: string;
  street_suffix: string;
  secondary_designator: string;
  secondary_number: string;
  city: string;
  state: string;
  zip_code: string;
  zip_code_plus_four: string;
}

export interface LobVerificationResult {
  id: string;
  deliverability: LobDeliverability;
  components: LobAddressComponents;
  lob_confidence_score: number;
}

export interface LobVerificationInput {
  primary_line: string;
  secondary_line?: string;
  city: string;
  state: string;
  zip_code: string;
}

export type MinimumAddressResolution = "state" | "zip" | "street" | "building";

/** Lob runs only for `street` and `building` — the widget's `needsVerification`. */
export function needsVerification(resolution: MinimumAddressResolution | undefined): boolean {
  return resolution === "street" || resolution === "building";
}

const DELIVERABILITY: ReadonlyArray<LobDeliverability> = [
  "deliverable",
  "deliverable_missing_unit",
  "deliverable_incorrect_unit",
  "deliverable_unnecessary_unit",
  "undeliverable",
];

/** Verify one address through `POST {baseUrl}/api/av`; null on ANY failure. */
export async function verifyAddress(
  baseUrl: string,
  address: LobVerificationInput,
  signal?: AbortSignal,
): Promise<LobVerificationResult | null> {
  try {
    const response = await fetch(`${baseUrl}/api/av`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      signal,
      body: JSON.stringify({
        primary_line: address.primary_line,
        ...(address.secondary_line ? { secondary_line: address.secondary_line } : {}),
        city: address.city,
        state: address.state,
        zip_code: address.zip_code,
      }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { result?: unknown };
    const data = body.result as Record<string, unknown> | null | undefined;
    if (!data || !DELIVERABILITY.includes(data.deliverability as LobDeliverability) || !data.components) {
      return null;
    }
    const c = data.components as Record<string, unknown>;
    const s = (key: string) => (typeof c[key] === "string" ? (c[key] as string) : "");
    return {
      id: typeof data.id === "string" ? data.id : "",
      deliverability: data.deliverability as LobDeliverability,
      components: {
        primary_number: s("primary_number"),
        street_name: s("street_name"),
        street_suffix: s("street_suffix"),
        secondary_designator: s("secondary_designator"),
        secondary_number: s("secondary_number"),
        city: s("city"),
        state: s("state"),
        zip_code: s("zip_code"),
        zip_code_plus_four: s("zip_code_plus_four"),
      },
      lob_confidence_score: typeof data.lob_confidence_score === "number" ? data.lob_confidence_score : 0,
    };
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------
// Verification record (formvu-web lib/lob/address-record.ts, 1:1)
// ----------------------------------------------------------------------

export type AddressVerificationStatus = "verified" | "unverified" | "not_attempted";

export type AddressVerificationReason =
  | "no_publishable_key"
  | "resolution_below_street"
  | "no_address_components"
  | "no_address_autocomplete"
  | "lob_unavailable"
  | "lob_error";

/** USPS delivery-point fact, not a building survey — see the widget's notes. */
export type PropertyUnitClass = "multi_unit" | "single_unit" | "unknown";

export interface AddressVerificationRecord {
  status: AddressVerificationStatus;
  unitClass: PropertyUnitClass;
  reason?: AddressVerificationReason;
  deliverability?: LobDeliverability;
  confidence?: number;
  unitDesignator?: string;
  unitNumber?: string;
  components?: LobAddressComponents;
  /** ISO 8601. */
  recordedAt: string;
}

/** Records per address field, keyed by field name — the `_addressVerification` sidecar. */
export type AddressVerificationRecordMap = Record<string, AddressVerificationRecord>;

const trimmed = (value: string | undefined): string | undefined => {
  const out = value?.trim();
  return out ? out : undefined;
};

export function classifyUnitClass(result: LobVerificationResult): PropertyUnitClass {
  switch (result.deliverability) {
    case "deliverable_missing_unit":
    case "deliverable_incorrect_unit":
      return "multi_unit";
    case "deliverable_unnecessary_unit":
      return "single_unit";
    case "deliverable":
      return trimmed(result.components.secondary_number) || trimmed(result.components.secondary_designator)
        ? "multi_unit"
        : "single_unit";
    case "undeliverable":
    default:
      return "unknown";
  }
}

interface BuildOptions {
  now?: string;
}

const stamp = (options?: BuildOptions): string => options?.now ?? new Date().toISOString();

export function recordVerified(result: LobVerificationResult, options?: BuildOptions): AddressVerificationRecord {
  return {
    status: "verified",
    unitClass: classifyUnitClass(result),
    deliverability: result.deliverability,
    confidence: result.lob_confidence_score,
    unitDesignator: trimmed(result.components.secondary_designator),
    unitNumber: trimmed(result.components.secondary_number),
    components: result.components,
    recordedAt: stamp(options),
  };
}

export function recordUnverified(
  reason: Extract<AddressVerificationReason, "lob_unavailable" | "lob_error">,
  options?: BuildOptions,
): AddressVerificationRecord {
  return { status: "unverified", unitClass: "unknown", reason, recordedAt: stamp(options) };
}

export function recordNotAttempted(
  reason: Extract<
    AddressVerificationReason,
    "no_publishable_key" | "resolution_below_street" | "no_address_components" | "no_address_autocomplete"
  >,
  options?: BuildOptions,
): AddressVerificationRecord {
  return { status: "not_attempted", unitClass: "unknown", reason, recordedAt: stamp(options) };
}

// ----------------------------------------------------------------------
// Relaxer (formvu-web form-fields/relax-address.ts, 1:1)
// ----------------------------------------------------------------------

const DIRECTIONAL_ALIASES: Record<string, string> = {
  N: "N",
  S: "S",
  E: "E",
  W: "W",
  NE: "NE",
  NW: "NW",
  SE: "SE",
  SW: "SW",
  NORTH: "N",
  SOUTH: "S",
  EAST: "E",
  WEST: "W",
  NORTHEAST: "NE",
  NORTHWEST: "NW",
  SOUTHEAST: "SE",
  SOUTHWEST: "SW",
};

function canonicalDirectional(token: string): string | null {
  return DIRECTIONAL_ALIASES[token.toUpperCase()] ?? null;
}

/**
 * Second-chance form of a primary line USPS rejected: drops a house-number
 * alpha suffix that duplicates the next directional ("700e E 9th" → "700 E
 * 9th") and doubled directionals ("700 E EAST 9th" → "700 E 9th"). Returns
 * the input unchanged when no rule applies.
 */
export function relaxPrimaryLine(line: string): string {
  if (!line) return line;
  const tokens = line.trim().split(/\s+/);
  if (tokens.length < 2) return line;

  const houseMatch = tokens[0]!.match(/^(\d+)([A-Za-z])$/);
  if (houseMatch && tokens[1]) {
    const suffixCanon = canonicalDirectional(houseMatch[2]!);
    const nextCanon = canonicalDirectional(tokens[1]);
    if (suffixCanon && nextCanon && suffixCanon === nextCanon) {
      tokens[0] = houseMatch[1]!;
    }
  }

  const out: string[] = [];
  for (const t of tokens) {
    const last = out[out.length - 1];
    if (last) {
      const lastCanon = canonicalDirectional(last);
      const currCanon = canonicalDirectional(t);
      if (lastCanon && currCanon && lastCanon === currCanon) continue;
    }
    out.push(t);
  }
  return out.join(" ");
}

// ----------------------------------------------------------------------
// Unit input (the widget's unit-modal rules)
// ----------------------------------------------------------------------

const UNIT_DESIGNATOR = /^(apt|suite|ste|unit|#|no|bldg|fl|rm)\b/i;

/** How a typed unit reads in the address: "5B" → "Unit 5B", "Apt 5B" kept. */
export function displayUnit(unit: string): string {
  return UNIT_DESIGNATOR.test(unit) ? unit : `Unit ${unit}`;
}

// A unit already written in the street line ("APT 5B", "#5B", "Unit B",
// "FL 2", "Apt PH"): a designator and an identifier with a digit or of one
// letter; two letters only after a designator that is never a street word
// (APT, UNIT, SUITE, #). "10 Space Rd" and "Ste. Genevieve" stay addresses.
const WRITTEN_UNIT =
  /(?:\b(?:apt|apartment|unit|ste|suite|bldg|building|fl|floor|rm|room|lot|spc|space|trlr|no)\b\.?|#)\s*(?:[a-z0-9-]*\d[a-z0-9-]*|[a-z])\b|(?:\b(?:apt|apartment|unit|suite)\b\.?|#)\s*[a-z]{2}\b/gi;

// A later comma part that is nothing but a unit (", APT 5B,"). No "FL" or
// "NO" here: past the street line "FL 33101" is Florida and its ZIP.
const UNIT_PART =
  /^(?:#|(?:apt|apartment|unit|ste|suite|bldg|building|floor|rm|room|lot|spc|space|trlr)\b\.?\s*#?)\s*(?:[a-z0-9-]*\d[a-z0-9-]*|[a-z]{1,2})$/i;

/**
 * The address text with the unit after the street line. A unit the text
 * already carries — in the street line, or as a part of its own — is
 * replaced, never kept beside it: "APT 5B" answered with 5C is "Unit 5C",
 * not two apartments. Nothing else is touched (the state and ZIP stay).
 */
export function withUnit(text: string, unit: string): string {
  const [street = "", ...rest] = text.split(",").map((part) => part.replace(/\s+/g, " ").trim());
  const parts = [street.replace(WRITTEN_UNIT, " ").replace(/\s+/g, " ").trim(), ...rest.filter((part) => !UNIT_PART.test(part))];
  return parts.filter(Boolean).join(", ").replace(/^([^,]+)/, `$1 ${displayUnit(unit)}`);
}

/**
 * Lob silently drops trailing junk from a unit ("7 a ffggg" → `7A`,
 * deliverable). A unit Lob only partly honored is not a verified unit.
 */
export function lobAteUnitJunk(unit: string, result: LobVerificationResult): boolean {
  const accepted = result.deliverability === "deliverable" || result.deliverability === "deliverable_unnecessary_unit";
  const userClean = unit
    .toUpperCase()
    .replace(/^(APARTMENT|APT|SUITE|STE|UNIT|NO|#|BLDG|FL|RM)\b/i, "")
    .replace(/[^A-Z0-9]/g, "");
  const lobClean = (result.components.secondary_number ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  return accepted && lobClean.length > 0 && userClean.length > lobClean.length && userClean.startsWith(lobClean);
}
